// macadamia-app/src/macadamia-core/contexts/SpatialContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

// ========================================
// TYPES & INTERFACES
// ========================================

export interface BlockPosition {
    id: string;
    type: string;
    bounds: DOMRect;
    pageIndex: number;
    relativePosition: {
        x: number;  // Pourcentage (0-100) dans la page
        y: number;  // Pourcentage (0-100) dans la page
    };
    absolutePosition: {
        top: number;
        left: number;
        width: number;
        height: number;
    };
    element: HTMLElement;
}

export interface PageInfo {
    index: number;
    bounds: DOMRect;
    blockIds: string[];
    element: HTMLElement;
    contentArea: {
        top: number;
        left: number;
        width: number;
        height: number;
    };
}

export interface SpatialZone {
    name: string;
    type: 'margin' | 'content' | 'header' | 'footer' | 'custom';
    bounds: {
        top: number;
        left: number;
        width: number;
        height: number;
    };
    pageIndex: number;
}

interface SpatialContextType {
    // État principal
    blocks: Map<string, BlockPosition>;
    pages: PageInfo[];
    zones: Map<string, SpatialZone>;
    isReady: boolean;

    // Helpers - Queries
    getBlockPosition: (blockId: string) => BlockPosition | null;
    getBlocksInPage: (pageIndex: number) => BlockPosition[];
    getBlocksInZone: (zoneName: string) => BlockPosition[];
    getNearestBlock: (x: number, y: number) => BlockPosition | null;
    getPageAtY: (scrollY: number) => number;

    // Helpers - Validation
    canPlaceElementAt: (x: number, y: number, width: number, height: number, pageIndex?: number) => boolean;
    isInContentArea: (x: number, y: number, pageIndex?: number) => boolean;

    // Helpers - Calculs
    getRelativePosition: (absoluteX: number, absoluteY: number, pageIndex: number) => { x: number; y: number } | null;
    getAbsolutePosition: (relativeX: number, relativeY: number, pageIndex: number) => { x: number; y: number } | null;

    // Gestion des zones
    registerZone: (name: string, zone: Omit<SpatialZone, 'name'>) => void;
    unregisterZone: (name: string) => void;
}

// ========================================
// CONSTANTES
// ========================================

const A4_DIMENSIONS = {
    WIDTH: 794,   // pixels à 96 DPI
    HEIGHT: 1122, // pixels à 96 DPI
    PADDING: 56   // padding interne des pages A4
};

// ========================================
// CONTEXTE
// ========================================

const SpatialContext = createContext<SpatialContextType | undefined>(undefined);

// ========================================
// PROVIDER
// ========================================

interface SpatialProviderProps {
    children: ReactNode;
}

export const SpatialProvider: React.FC<SpatialProviderProps> = ({ children }) => {
    // État
    const [blocks, setBlocks] = useState<Map<string, BlockPosition>>(new Map());
    const [pages, setPages] = useState<PageInfo[]>([]);
    const [zones, setZones] = useState<Map<string, SpatialZone>>(new Map());
    const [isReady, setIsReady] = useState(false);

    // ========================================
    // OBSERVATION DES BLOCS
    // ========================================

    useEffect(() => {
        let resizeObserver: ResizeObserver | null = null;
        let mutationObserver: MutationObserver | null = null;

        const measureBlocks = () => {
            const blockElements = document.querySelectorAll('[data-spatial-element="block"]');
            const newBlocks = new Map<string, BlockPosition>();

            blockElements.forEach((element) => {
                const el = element as HTMLElement;
                const blockId = el.dataset.blockId;
                const blockType = el.dataset.blockType || 'unknown';

                if (!blockId) return;

                const bounds = el.getBoundingClientRect();
                const pageIndex = findPageIndexForBounds(bounds);
                const relativePosition = calculateRelativePosition(bounds, pageIndex);

                newBlocks.set(blockId, {
                    id: blockId,
                    type: blockType,
                    bounds,
                    pageIndex,
                    relativePosition,
                    absolutePosition: {
                        top: bounds.top,
                        left: bounds.left,
                        width: bounds.width,
                        height: bounds.height
                    },
                    element: el
                });
            });

            setBlocks(newBlocks);
        };

        // ResizeObserver pour les changements de taille
        resizeObserver = new ResizeObserver(() => {
            measureBlocks();
        });

        // MutationObserver pour les ajouts/suppressions de blocs
        mutationObserver = new MutationObserver(() => {
            const blockElements = document.querySelectorAll('[data-spatial-element="block"]');
            blockElements.forEach((el) => {
                if (!resizeObserver) return;
                resizeObserver.observe(el);
            });
            measureBlocks();
        });

        // Observer le DOM
        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-spatial-element', 'data-block-id']
        });

        // Mesure initiale
        const blockElements = document.querySelectorAll('[data-spatial-element="block"]');
        blockElements.forEach((el) => resizeObserver?.observe(el));
        measureBlocks();

        return () => {
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
        };
    }, []);

    // ========================================
    // OBSERVATION DES PAGES
    // ========================================

    useEffect(() => {
        let resizeObserver: ResizeObserver | null = null;
        let mutationObserver: MutationObserver | null = null;

        const measurePages = () => {
            const pageElements = document.querySelectorAll('[data-spatial-element="page"]');
            const newPages: PageInfo[] = [];

            pageElements.forEach((element, idx) => {
                const el = element as HTMLElement;
                const pageIndex = parseInt(el.dataset.pageIndex || String(idx), 10);
                const bounds = el.getBoundingClientRect();

                newPages.push({
                    index: pageIndex,
                    bounds,
                    blockIds: [], // Sera calculé après
                    element: el,
                    contentArea: {
                        top: bounds.top + A4_DIMENSIONS.PADDING,
                        left: bounds.left + A4_DIMENSIONS.PADDING,
                        width: bounds.width - (A4_DIMENSIONS.PADDING * 2),
                        height: bounds.height - (A4_DIMENSIONS.PADDING * 2)
                    }
                });
            });

            setPages(newPages);
        };

        resizeObserver = new ResizeObserver(() => {
            measurePages();
        });

        mutationObserver = new MutationObserver(() => {
            const pageElements = document.querySelectorAll('[data-spatial-element="page"]');
            pageElements.forEach((el) => resizeObserver?.observe(el));
            measurePages();
        });

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-spatial-element', 'data-page-index']
        });

        const pageElements = document.querySelectorAll('[data-spatial-element="page"]');
        pageElements.forEach((el) => resizeObserver?.observe(el));
        measurePages();

        return () => {
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
        };
    }, []);

    // ========================================
    // CALCUL DES BLOCS PAR PAGE
    // ========================================

    useEffect(() => {
        if (pages.length === 0) {
            setIsReady(false);
            return;
        }

        const updatedPages = pages.map((page) => {
            const blocksInPage = Array.from(blocks.values())
                .filter((block) => block.pageIndex === page.index)
                .map((block) => block.id);

            return {
                ...page,
                blockIds: blocksInPage
            };
        });

        setPages(updatedPages);
        setIsReady(true);
    }, [blocks]);

    // ========================================
    // FONCTIONS UTILITAIRES INTERNES
    // ========================================

    const findPageIndexForBounds = (bounds: DOMRect): number => {
        // Trouver dans quelle page se trouve le bloc
        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];
            const pageBounds = page.bounds;

            // Un bloc appartient à une page si son centre Y est dans la page
            const blockCenterY = bounds.top + bounds.height / 2;

            if (
                blockCenterY >= pageBounds.top &&
                blockCenterY <= pageBounds.bottom
            ) {
                return i;
            }
        }

        // Par défaut, retourner la première page
        return 0;
    };

    const calculateRelativePosition = (
        bounds: DOMRect,
        pageIndex: number
    ): { x: number; y: number } => {
        const page = pages[pageIndex];
        if (!page) return { x: 0, y: 0 };

        const contentArea = page.contentArea;

        const x = ((bounds.left - contentArea.left) / contentArea.width) * 100;
        const y = ((bounds.top - contentArea.top) / contentArea.height) * 100;

        return {
            x: Math.max(0, Math.min(100, x)),
            y: Math.max(0, Math.min(100, y))
        };
    };

    // ========================================
    // HELPERS - QUERIES
    // ========================================

    const getBlockPosition = useCallback(
        (blockId: string): BlockPosition | null => {
            return blocks.get(blockId) || null;
        },
        [blocks]
    );

    const getBlocksInPage = useCallback(
        (pageIndex: number): BlockPosition[] => {
            return Array.from(blocks.values()).filter(
                (block) => block.pageIndex === pageIndex
            );
        },
        [blocks]
    );

    const getBlocksInZone = useCallback(
        (zoneName: string): BlockPosition[] => {
            const zone = zones.get(zoneName);
            if (!zone) return [];

            return Array.from(blocks.values()).filter((block) => {
                const blockBounds = block.absolutePosition;
                const zoneBounds = zone.bounds;

                // Vérifier si le bloc intersecte avec la zone
                return !(
                    blockBounds.left + blockBounds.width < zoneBounds.left ||
                    blockBounds.left > zoneBounds.left + zoneBounds.width ||
                    blockBounds.top + blockBounds.height < zoneBounds.top ||
                    blockBounds.top > zoneBounds.top + zoneBounds.height
                );
            });
        },
        [blocks, zones]
    );

    const getNearestBlock = useCallback(
        (x: number, y: number): BlockPosition | null => {
            let nearest: BlockPosition | null = null;
            let minDistance = Infinity;

            blocks.forEach((block) => {
                const centerX = block.bounds.left + block.bounds.width / 2;
                const centerY = block.bounds.top + block.bounds.height / 2;
                const distance = Math.sqrt(
                    Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
                );

                if (distance < minDistance) {
                    minDistance = distance;
                    nearest = block;
                }
            });

            return nearest;
        },
        [blocks]
    );

    const getPageAtY = useCallback(
        (scrollY: number): number => {
            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                if (scrollY >= page.bounds.top && scrollY <= page.bounds.bottom) {
                    return i;
                }
            }
            return 0;
        },
        [pages]
    );

    // ========================================
    // HELPERS - VALIDATION
    // ========================================

    const canPlaceElementAt = useCallback(
        (
            x: number,
            y: number,
            width: number,
            height: number,
            pageIndex: number = 0
        ): boolean => {
            const page = pages[pageIndex];
            if (!page) return false;

            const contentArea = page.contentArea;

            // Vérifier si l'élément reste dans la zone de contenu
            const fitsInContentArea =
                x >= contentArea.left &&
                y >= contentArea.top &&
                x + width <= contentArea.left + contentArea.width &&
                y + height <= contentArea.top + contentArea.height;

            if (!fitsInContentArea) return false;

            // Vérifier les collisions avec les blocs existants
            const hasCollision = Array.from(blocks.values()).some((block) => {
                if (block.pageIndex !== pageIndex) return false;

                const blockBounds = block.absolutePosition;
                return !(
                    x + width < blockBounds.left ||
                    x > blockBounds.left + blockBounds.width ||
                    y + height < blockBounds.top ||
                    y > blockBounds.top + blockBounds.height
                );
            });

            return !hasCollision;
        },
        [blocks, pages]
    );

    const isInContentArea = useCallback(
        (x: number, y: number, pageIndex: number = 0): boolean => {
            const page = pages[pageIndex];
            if (!page) return false;

            const contentArea = page.contentArea;
            return (
                x >= contentArea.left &&
                x <= contentArea.left + contentArea.width &&
                y >= contentArea.top &&
                y <= contentArea.top + contentArea.height
            );
        },
        [pages]
    );

    // ========================================
    // HELPERS - CALCULS
    // ========================================

    const getRelativePosition = useCallback(
        (
            absoluteX: number,
            absoluteY: number,
            pageIndex: number
        ): { x: number; y: number } | null => {
            const page = pages[pageIndex];
            if (!page) return null;

            const contentArea = page.contentArea;

            const x = ((absoluteX - contentArea.left) / contentArea.width) * 100;
            const y = ((absoluteY - contentArea.top) / contentArea.height) * 100;

            return {
                x: Math.max(0, Math.min(100, x)),
                y: Math.max(0, Math.min(100, y))
            };
        },
        [pages]
    );

    const getAbsolutePosition = useCallback(
        (
            relativeX: number,
            relativeY: number,
            pageIndex: number
        ): { x: number; y: number } | null => {
            const page = pages[pageIndex];
            if (!page) return null;

            const contentArea = page.contentArea;

            const x = contentArea.left + (relativeX / 100) * contentArea.width;
            const y = contentArea.top + (relativeY / 100) * contentArea.height;

            return { x, y };
        },
        [pages]
    );

    // ========================================
    // GESTION DES ZONES
    // ========================================

    const registerZone = useCallback(
        (name: string, zone: Omit<SpatialZone, 'name'>) => {
            setZones((prev) => new Map(prev).set(name, { name, ...zone }));
        },
        []
    );

    const unregisterZone = useCallback((name: string) => {
        setZones((prev) => {
            const newZones = new Map(prev);
            newZones.delete(name);
            return newZones;
        });
    }, []);

    // ========================================
    // VALEUR DU CONTEXTE (Mémoïsée)
    // ========================================

    const value = useMemo<SpatialContextType>(
        () => ({
            blocks,
            pages,
            zones,
            isReady,
            getBlockPosition,
            getBlocksInPage,
            getBlocksInZone,
            getNearestBlock,
            getPageAtY,
            canPlaceElementAt,
            isInContentArea,
            getRelativePosition,
            getAbsolutePosition,
            registerZone,
            unregisterZone
        }),
        [
            blocks,
            pages,
            zones,
            isReady,
            getBlockPosition,
            getBlocksInPage,
            getBlocksInZone,
            getNearestBlock,
            getPageAtY,
            canPlaceElementAt,
            isInContentArea,
            getRelativePosition,
            getAbsolutePosition,
            registerZone,
            unregisterZone
        ]
    );

    return (
        <SpatialContext.Provider value={value}>
            {children}
        </SpatialContext.Provider>
    );
};

// ========================================
// HOOK
// ========================================

export const useSpatial = (): SpatialContextType => {
    const context = useContext(SpatialContext);
    if (!context) {
        throw new Error('useSpatial must be used within a SpatialProvider');
    }
    return context;
};

// ========================================
// EXPORT PAR DÉFAUT
// ========================================

export default SpatialProvider;